<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type {
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { ILoginPayload, IUserLoginRequiredMfaCode } from '@modules/user/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import { TRANSLATION_GROUP_ANONYMOUS_MAIN } from '@modules/anonymous-main/constants';
import { isServerValidationError } from '@modules/core/errors';
import { FORGOT_PASSWORD_EMAIL_ROUTE_NAME } from '@modules/forgot-password/constants';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUP_LOGIN } from '@modules/login/constants';
import { REGISTRATION_ROUTE_NAME } from '@modules/registration/constants';
import StaticPageDialogButton from '@modules/static-pages/components/StaticPageDialogButton/StaticPageDialogButton.vue';
import { EStaticPageName } from '@modules/static-pages/enums';
import { useUserSignInMutation } from '@modules/user/mutations';

const {
  isLoading,
  mutateAsync,
  errors,
  clearError,
} = useUserSignInMutation();

const { getTranslation } = useI18n();

const isMfaEnabled = ref(false);

const formSchema = computed<FormSchema<ILoginPayload>>(() => {
  const schema: FormSchema<ILoginPayload> = {
    email: {
      type: EFormInputTypes.TEXT,
      inputType: 'email',
      label: getTranslation(TRANSLATION_GROUP_LOGIN, 'EMAIL').value,
      required: true,
      isHidden: isMfaEnabled.value,
      autocomplete: 'username',
    },
    password: {
      type: EFormInputTypes.PASSWORD,
      label: getTranslation(TRANSLATION_GROUP_LOGIN, 'PASSWORD').value,
      required: true,
      isHidden: isMfaEnabled.value,
      autocomplete: 'current-password',
    },
  };

  if (isMfaEnabled.value) {
    schema.mfa_code = {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_LOGIN, 'MFA_CODE').value,
      required: true,
    };
  }

  return schema;
});

function onInteracted(): void {
  clearError('email');
  clearError('password');

  if (isMfaEnabled.value) {
    clearError('mfa_code');
  }
}

async function onSubmit(values: ILoginPayload): Promise<void> {
  try {
    await mutateAsync(values);
  } catch (error) {
    if (isServerValidationError<object, IUserLoginRequiredMfaCode>(error)) {
      if (error.response.required_mfa_code) {
        isMfaEnabled.value = true;
      }
      return;
    }

    throw error;
  }
}
</script>

<template>
  <div v-data-qa-auto-id="'LoginPage'" class="mb-6 text-center text-3xl font-light uppercase">
    {{ $getTranslation(TRANSLATION_GROUP_LOGIN, 'TITLE') }}
  </div>
  <VForm
    v-data-test="{ el: 'login-form' }"
    :schema="formSchema"
    :submit-btn-label="$getTranslation(TRANSLATION_GROUP_LOGIN, 'SUBMIT')"
    :is-loading="isLoading"
    :custom-errors="errors"
    is-buttons-centered
    class="min-w-[300px]"
    @submit="onSubmit"
    @interacted="onInteracted"
  >
    <template #prefooter>
      <div class="flex w-full items-center justify-center gap-2">
        <VButton
          v-data-test="{ el: 'btn-forgot-password' }"
          :add-merchant="false"
          :to="{
            name: FORGOT_PASSWORD_EMAIL_ROUTE_NAME,
          }"
          :label="$getTranslation(TRANSLATION_GROUP_LOGIN, 'PASSWORD_NEW')"
          text
        />
        <VButton
          v-data-test="{ el: 'btn-registration' }"
          :add-merchant="false"
          :to="{
            name: REGISTRATION_ROUTE_NAME,
          }"
          :label="$getTranslation(TRANSLATION_GROUP_ANONYMOUS_MAIN, 'SIGN_UP')"
          text
        />
      </div>
    </template>
  </VForm>
  <div class="mx-auto mt-6">
    <StaticPageDialogButton
      :page="EStaticPageName.TERMS"
    />
  </div>
</template>
